import { useUserId } from "src/utils/UserIdProvider";
import { buildGetRequest, useFetchApi } from "src/ssc-api/useFetchApi";
import type {
  LastSeenProduct,
  LastSeenProductsResponse
} from "@silversea-ssc/dotcom-api-types/LastSeenProducts";
import {
  LastSeenProductsContent,
  LastSeenProductsContents,
  useLastSeenProductsContent
} from "components/LastSeenProducts/useLastSeenProductsContent";
import { useState } from "react";
import { isPresent } from "src/utils/checks";
import { isEnvFeatureEnabled } from "src/utils/feature-flags";

export type LastSeenProductsResult = ReturnType<typeof useLastSeenProducts>;

type Arguments = {
  enabled?: boolean;
  destinationWebCode?: string;
};
export const useLastSeenProducts = (
  { enabled = true, destinationWebCode }: Arguments = { enabled: true }
) => {
  const userId = useUserId();
  const lastSeenProductsData = useLoadLastSeenProductsData({
    userId,
    enabled,
    destinationWebCode
  });

  const responseContent = useLastSeenProductsContent(
    lastSeenProductsData?.map((x) => x.cruiseCode)
  );
  const lastSeenProductsContent_ =
    responseContent.status === "success"
      ? responseContent.data?.result
      : undefined;
  const products = lastSeenProductsContent(
    lastSeenProductsData ?? [],
    lastSeenProductsContent_ ?? []
  );
  const [deletedProducts, setDeletedProducts] = useState<string[]>([]);
  const lastSeenProducts = products
    .filter(
      ([lastSeenProduct]) =>
        !deletedProducts.includes(lastSeenProduct.cruiseCode)
    )
    .slice(0, 6);

  return {
    lastSeenProducts,
    deleteProduct: (cruiseCode: string) =>
      setDeletedProducts((prev) => [...prev, cruiseCode]),
    hasLastSeenProducts: lastSeenProducts.length > 0
  };
};

type LastSeenProductWithContent = [LastSeenProduct, LastSeenProductsContent];
const lastSeenProductsContent = (
  lastSeenProducts: LastSeenProductsResponse,
  content: LastSeenProductsContents
): readonly LastSeenProductWithContent[] =>
  lastSeenProducts
    .map((x): LastSeenProductWithContent | undefined => {
      const maybeContent = content.find((c) => c.cruiseCode === x.cruiseCode);
      return maybeContent ? [x, maybeContent] : undefined;
    })
    .filter(isPresent);

const useLoadLastSeenProductsData = ({
  userId,
  enabled,
  destinationWebCode
}: Arguments & { userId: string | undefined }) => {
  const withMockedData = isEnvFeatureEnabled(
    process.env.GATSBY_MOCKED_LAST_SEEN_CRUISES
  );

  const response = useFetchApi(
    userId && enabled && !withMockedData
      ? new Request(
          buildGetRequest("lastSeenProducts", { destinationWebCode }),
          {
            method: "get",
            credentials: "include"
          }
        )
      : undefined,
    undefined,
    false
  );

  if (withMockedData) return lastSeenProductsForDev();

  return response.status === "success"
    ? (response.data as LastSeenProductsResponse)
    : undefined;
};

const lastSeenProductsForDev = (): LastSeenProductsResponse => {
  return [
    {
      cruiseCode: "OR250301007",
      destinationWebCode: "destination-23"
    },
    {
      cruiseCode: "DA260106140",
      destinationWebCode: "destination-101"
    },
    {
      cruiseCode: "SL241004043",
      destinationWebCode: "destination-100"
    }
  ];
};
